import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Alert,
  ActionIcon,
} from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";

function EditOperatingLevelTeamsModal({
  isOpen,
  onClose,
  entity,
  allOrgLevels = [],
  baseOrgLevelName = "",
  sellingYearId,
  onTeamsUpdated,
}) {
  const { getVerifiedJWT } = useAuth();
  const [error, setError] = useState(null);
  const [localAssigned, setLocalAssigned] = useState([]);
  const [localAvailable, setLocalAvailable] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const sortByName = teams =>
    [...teams].sort((a, b) =>
      (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
    );

  const orgLevelLabel = baseOrgLevelName || "Teams";

  useEffect(() => {
    const assigned = Array.isArray(entity?.assigned_org_levels)
      ? entity.assigned_org_levels
      : [];
    const assignedTeamIds = new Set(assigned.map(a => a.org_level_entity_id));
    const assignedTeams = allOrgLevels.filter(t => assignedTeamIds.has(t.id));
    const available = allOrgLevels.filter(t => !assignedTeamIds.has(t.id));
    setLocalAssigned(sortByName(assignedTeams));
    setLocalAvailable(sortByName(available));
    setHasChanges(false);
  }, [entity, allOrgLevels]);

  const updateTeam = async (team, include) => {
    if (!entity?.id || team?.id == null) return;

    setError(null);

    if (include) {
      setLocalAssigned(prev => sortByName([...prev, team]));
      setLocalAvailable(prev => sortByName(prev.filter(t => t.id !== team.id)));
    } else {
      setLocalAvailable(prev => sortByName([...prev, team]));
      setLocalAssigned(prev => sortByName(prev.filter(t => t.id !== team.id)));
    }

    setHasChanges(true);

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/operations/entity/team"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            operation_entity_id: entity.id,
            org_level_entity_id: team.id,
            include,
            selling_year_id: sellingYearId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update teams");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClose = () => {
    setError(null);
    if (hasChanges && onTeamsUpdated) {
      onTeamsUpdated();
    }
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={`Edit ${orgLevelLabel} - ${entity?.name || ""}`}
      size="lg"
      centered
    >
      <Stack gap="lg">
        {error && (
          <Alert variant="light" color="red" title="Error">
            {error}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            {orgLevelLabel} ({localAssigned.length})
          </Title>
          {localAssigned.length > 0 ? (
            <Stack gap="sm">
              {localAssigned.map((team, index) => (
                <Group
                  key={team.id ?? index}
                  gap="xs"
                  p="xs"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Text fw={500} size="sm" style={{ flex: 1 }}>
                    {team.name}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={() => updateTeam(team, false)}
                  >
                    <IconMinus size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No {orgLevelLabel.toLowerCase()} assigned
            </Text>
          )}
        </Paper>

        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Available {orgLevelLabel} ({localAvailable.length})
          </Title>
          {localAvailable.length > 0 ? (
            <Stack gap="sm">
              {localAvailable.map((team, index) => (
                <Group
                  key={team.id ?? index}
                  gap="xs"
                  p="xs"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Text fw={500} size="sm" style={{ flex: 1 }}>
                    {team.name}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="green"
                    onClick={() => updateTeam(team, true)}
                  >
                    <IconPlus size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No additional {orgLevelLabel.toLowerCase()} available
            </Text>
          )}
        </Paper>

        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditOperatingLevelTeamsModal;
